<template>
  <div v-if="tab === 0">
    <paginated-data-table
      :headers="headers"
      :search="search"
      :query="query"
      :loading="loading"
      :custom-items-per-page="10"
      :query-variables="queryVariables"
      :graphql-uri="$microservicesUrls['sales']"
      :query-name="queryName"
      :single-item="isMariaCampaignFinished"
    >
      <template #item="{ item }">
        <tr height="46">
          <td>
            <v-radio-group :key="item._id" v-model="campaign.offerSelected" column hide-details>
              <v-radio
                label=""
                color="primary"
                :value="item._id"
                :disabled="disabled || disableIfIsCashback(item) || disableIfIsNotDefault(item)"
                @click="toggleOfferSelected(item)"
              />
            </v-radio-group>
          </td>

          <td>
            {{ getStatusLabel(item.status).text }}
          </td>

          <td>
            <mf-tooltip max-width="250" top variant="secondary">
              <template #activator>
                <span v-if="!disabled && !disableIfIsCashback(item) && !disableIfIsNotDefault(item)">
                  <a class="link" @click="toggleOfferDialog(item._id)">
                    {{ putEllipsis(item.name, 15) }}
                  </a>
                </span>
                <span v-else>
                  {{ putEllipsis(item.name, 15) }}
                </span>
              </template>
              <span>{{ item.name }}</span>
            </mf-tooltip>
          </td>

          <td>
            <mf-tooltip top max-width="250" variant="secondary">
              <template #activator>
                <span>
                  {{ putEllipsis(item.description, 20) }}
                </span>
              </template>
              <span>{{ item.description }}</span>
            </mf-tooltip>
          </td>

          <td>
            <mf-tooltip v-if="item.units.length > 0" right variant="secondary">
              <template #activator>
                <span>
                  {{ getUnits(item) }}
                </span>
              </template>
              <span v-for="(unit, index) in item.units" :key="index" style="display: block">
                {{ unit.name }}
              </span>
            </mf-tooltip>
            <span v-else>-</span>
          </td>

          <td>{{ item.products_count }}</td>
          <td>{{ item.segments.length ? 'SIM' : '-' }}</td>
          <td>{{ item.activatable ? 'Ativável' : 'Livre' }}</td>

          <td>
            <mf-tooltip v-if="!isMariaCampaignFinished" top max-width="300px" variant="secondary">
              <template #activator>
                <span>{{ getOfferTypes(item.types).length === 1 ? putEllipsis(getOfferTypes(item.types)[0], 23) : 'Múltiplas' }}</span>
              </template>
              <span>{{ getOfferTypesTooltip(item.types) }}</span>
            </mf-tooltip>

            <mf-tooltip v-else top max-width="300px" variant="secondary">
              <template #activator>
                <span>{{ putEllipsis(getOfferTypesTextBySingleOfferSale(item), 23) }}</span>
              </template>
              <span> {{ getOfferTypesTooltipBySingleOfferSale(item) }}</span>
            </mf-tooltip>
          </td>

          <td v-if="showTooltip(item.status)">
            <mf-tooltip top variant="secondary" :disabled="!showTooltip(item.status)">
              <template #activator>
                <span v-if="showTooltip(item.status)">{{ getPeriod(item.start_date, item.end_date) }}</span>
                <span v-else>{{ getPeriod(item.start_date, item.end_date) }}</span>
              </template>
              <span v-if="showTooltip(item.status)">{{ getCampaignDuration(item) }}</span>
            </mf-tooltip>
          </td>
        </tr>
      </template>
    </paginated-data-table>
    <campaign-details-dialog
      :key="`campaignDetails-${renderKeyDialog}`"
      v-model="isOfferDialogOpen"
      :disabled="disabled"
      :campaign-id="campaignId"
      :selected-type="campaign.offerType"
      @select-oferta="selectOferta"
      @reset-modal-counter="resetModalCounter"
    />
  </div>
</template>

<script>
import { DateTime } from 'luxon'

import PaginatedDataTable from '@/components/PaginatedDataTable.vue'
import CampaignDetailsDialog from '@/modules/maria/campanha-da-maria/views/components/CampaignDetailsDialog.vue'
import { getStatusLabel, getUnits, getPeriod, putEllipsis } from '@/config/helpers.js'
import { QUERY_LIST_SALES_CAMPAIGNS, QUERY_SALES_CAMPAIGN_BY_ID } from '@/modules/campanhas/gestao-ofertas-v2/graphql'
import { getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'

export default {
  name: 'FormMariaCampaignTab1',

  components: {
    PaginatedDataTable,
    CampaignDetailsDialog,
  },

  props: {
    tab: {
      type: Number,
      required: true,
    },
    campaign: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isMariaCampaignFinished: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    search: '',
    campaignId: '',
    DateTime: DateTime,
    showTooltipDescription: {},
    renderKeyDialog: 0,
    isOfferDialogOpen: false,
  }),

  computed: {
    query() {
      return this.isMariaCampaignFinished ? QUERY_SALES_CAMPAIGN_BY_ID : QUERY_LIST_SALES_CAMPAIGNS
    },

    queryVariables() {
      if (this.isMariaCampaignFinished) return { id: this.campaign.offerSelected }
      return {
        status: ['active', 'scheduled'],
        franchise: this.franchisee,
        includeAuto: false,
        units: this.units ? this.units : [],
      }
    },

    queryName() {
      return this.isMariaCampaignFinished ? 'offerSchedule' : 'offerSchedules'
    },

    headers() {
      return [
        {
          text: '',
          value: '_id',
          width: '50px',
        },
        {
          text: 'Status',
          value: 'status',
          width: '6%',
        },
        {
          text: 'Nome',
          value: 'name',
          width: '13%',
        },
        {
          text: 'Descrição',
          value: 'description',
          width: '17.5%',
        },
        {
          text: 'Lojas',
          value: 'units',
          width: '80px',
        },
        {
          text: 'Produtos',
          value: 'products_count',
          width: '4%',
        },
        {
          text: 'Personalizável',
          value: 'is_personalized',
          width: '5%',
          sortable: false,
        },
        {
          text: 'Modelo',
          value: 'activatable',
          width: '7%',
        },
        {
          text: 'Formato',
          value: 'types',
          width: '15%',
        },
        {
          text: 'Validade',
          value: 'start_date',
          width: '180px',
        },
      ]
    },
    defaultShotConfigs() {
      const shotConfiguration = {
        whatsapp: {
          name: 'Whatsapp',
          icon: 'whatsapp',
          appChannelOptions: {
            toggleAppActivated: false,
            toggleAppExpanded: false,
          },
          targeting_mode: '',
          credits: '',
          message: {
            text: '',
            send_date: '',
            attachment: {
              data_url: '',
              thumbnail_url: '',
              mimetype: '',
              filename: '',
            },
          },
        },
        sms: {
          name: 'SMS',
          icon: 'sms',
          appChannelOptions: {
            toggleAppActivated: false,
            toggleAppExpanded: false,
          },
          send_date: '',
          text: '',
          credits: '',
          error: false,
        },
        push: {
          name: 'Push',
          icon: 'smartphone',
          appChannelOptions: {
            toggleAppActivated: false,
            toggleAppExpanded: false,
          },
          title: '',
          send_date: '',
          text: '',
          credits: '',
          inner_message_title: '',
          inner_message_text: '',
          innerMessage: false,
          image_url: '',
        },
        email: {
          name: 'E-mail',
          icon: 'email',
          appChannelOptions: {
            toggleAppActivated: false,
            toggleAppExpanded: false,
          },
          credits: '',
          message: {
            send_date: '',
            send_time: '',
            email_configuration_type: '',
            processed_html: '',
            subject: '',
            simple_email_configuration: {
              subject: '',
              header_image: null,
              text: '',
              image: null,
              click_to_call_button_text: '',
              click_to_call_button_link: '',
              footer: '',
            },
            html_email_configuration: {
              url: '',
              filename: '',
              has_uploaded_file: '',
              uploaded_at: '',
            },
          },
        },
      }
      return shotConfiguration
    },
  },

  watch: {
    isOfferDialogOpen() {
      this.renderKeyDialog += 1
    },
  },

  methods: {
    putEllipsis,
    getStatusLabel,
    getPeriod,
    getUnits,
    disableIfIsNotDefault(campaign) {
      return campaign?.flash
    },
    disableIfIsCashback(campaign) {
      const { types } = campaign
      const allowedTypes = ['general', 'takepay', 'percentage', 'combo']
      const hasAllowedType = types.some((type) => allowedTypes.includes(type))

      return !hasAllowedType
    },

    resetModalCounter() {
      this.$emit('reset-modal-counter')
    },

    showTooltip(status) {
      return status === 'active' || status === 'scheduled'
    },

    async selectOferta(offerSelected) {
      const { campaign, offerType } = offerSelected
      await this.toggleOfferSelected(campaign, offerType)
    },

    getCampaignDuration(item) {
      if (item.status === 'active') {
        const { days: endCampaign } = DateTime.fromISO(item.end_date).diffNow(['days']).toObject()
        return `A campanha terminará em ${parseInt(endCampaign)} dia(s)`
      } else if (item.status === 'scheduled') {
        const { days: initCampaign } = DateTime.fromISO(item.start_date).diffNow(['days']).toObject()
        return `A campanha iniciará em ${parseInt(initCampaign)} dia(s)`
      }
    },

    toggleOfferDialog(campaignId) {
      this.campaignId = campaignId
      this.isOfferDialogOpen = true
    },

    async toggleOfferSelected(offerSelected, offerType = '') {
      const sameCampaignSelected = this.campaign.offerSelected === offerSelected._id && this.campaign.offerSelected.offerType === offerType
      if (this.campaign && this.campaign.shotConfiguration && !sameCampaignSelected) {
        const hasSomeChannelConfigured = Object.entries(this.campaign.shotConfiguration).some((channel) => channel[1].appChannelOptions.toggleAppActivated)
        if (hasSomeChannelConfigured && !(await this.verifyAndConfirmDeleteStep4Messages())) return
        this.campaign.shotConfiguration = this.defaultShotConfigs
      }
      if (offerType) {
        this.campaign.offerType = offerType
        this.campaign.offerSelected = offerSelected._id
        this.campaign.personalized = false
        this.$emit('save-campaign')
      } else {
        this.campaign.offerType = ''
        if (offerSelected.types.length === 1) this.campaign.offerType = offerSelected.types[0]
        // toggle offer selected
        this.campaign.offerSelected = this.campaign.offerSelected === offerSelected._id ? '' : offerSelected._id
        this.campaign.personalized = !!offerSelected.segments.length
      }
    },

    async verifyAndConfirmDeleteStep4Messages() {
      const confirmed = await this.$confirm({
        message:
          'Alterando o formato de oferta os textos inseridos nos canais serão removidos e não poderão ser recuperados. Você deverá escrever um novo texto que siga os padrões do novo formato escolhido',
        confirmText: 'Continuar',
        cancelText: 'Cancelar',
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) this.resetModalCounter()
      return confirmed
    },

    getOfferTypes(types) {
      const offerTypes = []
      types.forEach((type) => offerTypes.push(getProductFormatTypeLabel(type)))
      return offerTypes
    },

    getOfferTypesTooltip(types) {
      return this.getOfferTypes(types).join(', ')
    },

    getOfferTypesTooltipBySingleOfferSale(campaign) {
      const types = this.getOfferTypesBySingleOfferSale(campaign)
      return this.getOfferTypes(types).join(', ')
    },

    getOfferTypesTextBySingleOfferSale(campaign) {
      const types = this.getOfferTypesBySingleOfferSale(campaign)
      if (types.length > 1) return 'Múltiplas'
      return this.getOfferTypes(types)[0]
    },

    getOfferTypesBySingleOfferSale(campaign) {
      const {
        combo_offer_sales,
        offer_sales,
        percentage_offer_sales,
        takepay_offer_sales,
        cashback_percentage_offer_sales,
        cashback_absolute_offer_sales,
        points_offer_sales,
      } = campaign
      const types = []
      if (offer_sales?.length) types.push('general')
      if (combo_offer_sales?.length) types.push('combo')
      if (percentage_offer_sales?.length) types.push('percentage')
      if (takepay_offer_sales?.length) types.push('takepay')
      if (cashback_percentage_offer_sales?.length) types.push('cashback_percentage')
      if (cashback_absolute_offer_sales?.length) types.push('cashback_absolute')
      if (points_offer_sales?.length) types.push('points_offer_sales')
      return types
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-data-table-header {
  background-color: $cinza-l90;

  th.text-start {
    white-space: pre;
  }
}

::v-deep tbody > tr {
  line-height: 50px;
  overflow: hidden;
  height: 50px;

  td {
    line-height: 50px;
    overflow: hidden;
    height: 50px;
  }
}

::v-deep th,
::v-deep td {
  padding: 0 8px !important;
}

::v-deep .v-radio {
  justify-content: center;
}

::v-deep .v-input--selection-controls {
  margin: 0;
  padding: 0;
}
</style>
